import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Activity, TrendingUp, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI } from '../../services/api';
import { Loading, ErrorMessage, StatCard } from '../Common/CommonComponents';
//import YearDistributionChart from '../Charts/YearDistributionChart';
// import DomainEnrollmentChart from '../Charts/DomainEnrollmentChart';
// import MultiDomainChart from '../Charts/MultiDomainChart';
import WeeklyProgressChart from '../Charts/WeeklyProgressChart';
import TaskTrendsChart from '../Charts/TaskTrendsChart';
// import DomainProgressTable from '../Charts/DomainProgressTable';
import StudentProgressTable from './StudentProgressTable';
import './DashboardNew.css';

const DashboardNew = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await dashboardAPI.getAllDashboardData();
      
      if (result.success) {
        setDashboardData(result.data);
      } else {
        setError(result.error || 'Failed to load dashboard data');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Loading message="Loading dashboard data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div style={{ padding: '32px' }}>
          <ErrorMessage message={error} onRetry={fetchDashboardData} />
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-container">
        <div style={{ padding: '32px' }}>
          <ErrorMessage message="No data available" onRetry={fetchDashboardData} />
        </div>
      </div>
    );
  }

  const { overview, 
   // yearDistribution, 
    weeklyProgress, taskTrends } = dashboardData;

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div>
            <h1 className="header-title">Admin Dashboard</h1>
            <p className="header-subtitle">
              Welcome back, {user?.full_name || 'Administrator'}
            </p>
          </div>
          <button onClick={handleLogout} className="logout-button">
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Section 1: Student Overview - Cards with Numbers */}
        <section className="dashboard-section">
          <h2 className="section-title">Student Overview</h2>
          <div className="stats-grid">
            <StatCard
              title="Total Students"
              value={overview.total_students.toLocaleString()}
              subtitle="Across all departments"
              icon={Users}
              color="#3b82f6"
            />
            <StatCard
              title="Active (Last 7 days)"
              value={overview.active_last_7_days.toLocaleString()}
              subtitle={`${((overview.active_last_7_days / overview.total_students) * 100).toFixed(1)}% of total`}
              icon={Activity}
              color="#10b981"
            />
            <StatCard
              title="Active (Last 30 days)"
              value={overview.active_last_30_days.toLocaleString()}
              subtitle={`${((overview.active_last_30_days / overview.total_students) * 100).toFixed(1)}% of total`}
              icon={Activity}
              color="#f59e0b"
            />
            <StatCard
              title="Inactive (30+ days)"
              value={overview.inactive_30_plus_days.toLocaleString()}
              subtitle={`${((overview.inactive_30_plus_days / overview.total_students) * 100).toFixed(1)}% of total`}
              icon={TrendingUp}
              color="#ef4444"
            />
          </div>
        </section>

        {/* Section 2: Year Distribution */}
        {/* <section className="dashboard-section">
          <div className="charts-grid-2">
            <div className="chart-card">
              <YearDistributionChart data={yearDistribution} />
            </div>
          </div>
        </section> */}

        {/* Section 3: Domain Enrollments */}
        {/*
        <section className="dashboard-section">
          <div className="chart-card">
            <DomainEnrollmentChart data={domainEnrollments} />
          </div>
        </section>
        */}

        {/* Section 5: Weekly Progress Tracking */}
        <section className="dashboard-section">
          <div className="chart-card">
            <WeeklyProgressChart data={weeklyProgress} />
          </div>
        </section>

        {/* Section 6: Task Completions - Last 10 Days */}
        <section className="dashboard-section">
          <div className="chart-card">
            <TaskTrendsChart data={taskTrends} />
          </div>
        </section>

        {/* Section 7: Domain-wise Progress Table */}
        {/*
        <section className="dashboard-section">
          <div className="chart-card">
            <DomainProgressTable data={domainProgress} />
          </div>
        </section>
        */}

        {/* Section 8: Student Progress Tracker */}
        <section className="dashboard-section">
          <StudentProgressTable />
        </section>

        {/* Last Updated Info */}
        <section className="dashboard-section">
          <div className="info-box">
            <p className="info-text">
              <strong>Last Updated:</strong> {new Date(overview.last_updated).toLocaleString()}
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default DashboardNew;
